import { onMounted, onUnmounted, ref, type Ref } from 'vue'
import { useForm } from 'vee-validate'

interface AddressComponents {
  street_number?: string
  route?: string
  locality?: string
  administrative_area_level_1?: string
  postal_code?: string
  country?: string
}

interface AutocompleteOptions {
  inputRef: Ref<HTMLInputElement | null>
  onPlaceSelected: (addressComponents: AddressComponents) => void
}

export function useGooglePlacesAutocomplete({ inputRef, onPlaceSelected }: AutocompleteOptions) {
  const autocomplete = ref<google.maps.places.Autocomplete | null>(null)
  const isLoaded = ref(false)

  const initAutocomplete = () => {
    if (!inputRef.value) {
      console.warn('Input ref is not available')
      return
    }

    // Check if Google Maps API is loaded
    if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
      console.warn('Google Maps API is not loaded yet')
      return
    }

    try {
      // Initialize autocomplete with worldwide support (no country restrictions)
      autocomplete.value = new google.maps.places.Autocomplete(inputRef.value, {
        types: ['address'],
        fields: ['address_components']
      })

      // Listen for place selection
      autocomplete.value.addListener('place_changed', () => {
        const place = autocomplete.value?.getPlace()

        console.log('Place selected:', place)

        if (!place || !place.address_components) {
          console.warn('No place details available')
          return
        }

        console.log('Address components:', place.address_components)

        // Parse address components - Two-pass approach
        // Pass 1: Collect all potential city values
        const addressComponents: AddressComponents = {}
        const cityOptions: { [key: string]: string } = {}
        let administrativeAreaLevel2 = ''

        place.address_components.forEach((component) => {
          const componentType = component.types[0]

          switch (componentType) {
            case 'street_number':
              addressComponents.street_number = component.long_name
              break
            case 'route':
              addressComponents.route = component.long_name
              break
            case 'locality':
              cityOptions.locality = component.long_name
              break
            case 'sublocality':
            case 'sublocality_level_1':
              cityOptions.sublocality = component.long_name
              break
            case 'neighborhood':
              cityOptions.neighborhood = component.long_name
              break
            case 'administrative_area_level_2':
              // Store for NYC borough detection (e.g., "Kings County" = Brooklyn)
              administrativeAreaLevel2 = component.long_name
              break
            case 'administrative_area_level_3':
              cityOptions.administrative_area_level_3 = component.long_name
              break
            case 'administrative_area_level_1':
              addressComponents.administrative_area_level_1 = component.short_name
              break
            case 'postal_code':
              addressComponents.postal_code = component.long_name
              break
            case 'country':
              addressComponents.country = component.short_name
              break
          }
        })

        // NYC-specific logic: Map county names to borough names
        // This handles cases where Google doesn't provide sublocality_level_1
        const nycCountyToBoroughMap: { [key: string]: string } = {
          'Kings County': 'Brooklyn',
          'Queens County': 'Queens',
          'New York County': 'Manhattan',
          'Bronx County': 'Bronx',
          'Richmond County': 'Staten Island'
        }

        // Check if this is a NYC address and map county to borough
        if (administrativeAreaLevel2 && nycCountyToBoroughMap[administrativeAreaLevel2]) {
          const borough = nycCountyToBoroughMap[administrativeAreaLevel2]
          // Only use borough if we don't already have a sublocality
          if (!cityOptions.sublocality) {
            cityOptions.sublocality = borough
            console.log(`NYC detected: Mapped ${administrativeAreaLevel2} to ${borough}`)
          }
        }

        // Pass 2: Select the best city value based on priority
        // Priority: locality > sublocality > neighborhood > administrative_area_level_3
        if (cityOptions.locality) {
          addressComponents.locality = cityOptions.locality
          console.log('Using locality as city:', cityOptions.locality)
        } else if (cityOptions.sublocality) {
          addressComponents.locality = cityOptions.sublocality
          console.log('Using sublocality as city:', cityOptions.sublocality)
        } else if (cityOptions.neighborhood) {
          addressComponents.locality = cityOptions.neighborhood
          console.log('Using neighborhood as city:', cityOptions.neighborhood)
        } else if (cityOptions.administrative_area_level_3) {
          addressComponents.locality = cityOptions.administrative_area_level_3
          console.log('Using administrative_area_level_3 as city:', cityOptions.administrative_area_level_3)
        }

        console.log('City options found:', cityOptions)
        console.log('Administrative Area Level 2:', administrativeAreaLevel2)
        console.log('Parsed address components:', addressComponents)

        // Call the callback with parsed address components
        onPlaceSelected(addressComponents)
      })

      isLoaded.value = true
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error)
    }
  }

  const waitForGoogleMaps = () => {
    // Check if Google Maps is already loaded
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      initAutocomplete()
      return
    }

    // Wait for Google Maps to load
    const checkInterval = setInterval(() => {
      if (typeof google !== 'undefined' && google.maps && google.maps.places) {
        clearInterval(checkInterval)
        initAutocomplete()
      }
    }, 100)

    // Timeout after 10 seconds
    setTimeout(() => {
      clearInterval(checkInterval)
      if (!isLoaded.value) {
        console.error('Google Maps API failed to load within 10 seconds')
      }
    }, 10000)
  }

  onMounted(() => {
    // Wait a bit for the input to be rendered
    setTimeout(() => {
      waitForGoogleMaps()
    }, 100)
  })

  onUnmounted(() => {
    // Clean up autocomplete listeners
    if (autocomplete.value) {
      google.maps.event.clearInstanceListeners(autocomplete.value)
    }
  })

  return {
    autocomplete,
    isLoaded
  }
}


